import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, Save, Building2, MapPin, Percent, Calendar, RefreshCw, Edit2, X
} from 'lucide-react';
import { toast } from 'sonner';

const STAGES = [
  'Prospect',
  'Intro Meeting Done',
  'Commercial Discussion',
  'Terms Locked',
  'Agreement Signed',
  'Live',
  'Inactive'
];

const BDProjectForm = ({ user }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedBuilderId = searchParams.get('builder_id');
  
  const isNew = projectId === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [builders, setBuilders] = useState([]);
  const [bdOwners, setBdOwners] = useState([]);
  
  const [formData, setFormData] = useState({
    builder_id: preselectedBuilderId || '',
    project_name: '',
    location: '',
    brokerage_percent: '',
    exclusive_or_open: 'Open',
    special_incentives: '',
    agreement_valid_till: '',
    notes: '',
    assigned_bd_owner: '',
    stage: 'Prospect',
  });
  
  const [project, setProject] = useState(null);

  useEffect(() => {
    fetchBuilders();
    fetchBdOwners();
    if (!isNew) {
      fetchProject();
    }
  }, [projectId]);

  const fetchBuilders = async () => {
    try {
      const res = await api.getBuilders({ status: 'Active', limit: 100 });
      setBuilders(res.data.builders || []);
    } catch (error) {
      console.error('Failed to load builders:', error);
    }
  };

  const fetchBdOwners = async () => {
    try {
      const res = await api.getBDOwners();
      setBdOwners(res.data.bd_owners || []);
    } catch (error) {
      console.error('Failed to load BD owners:', error);
    }
  };

  const fetchProject = async () => {
    setLoading(true);
    try {
      const res = await api.getBDProject(projectId);
      setProject(res.data);
      setFormData({
        builder_id: res.data.builder_id || '',
        project_name: res.data.project_name || '',
        location: res.data.location || '',
        brokerage_percent: res.data.brokerage_percent || '',
        exclusive_or_open: res.data.exclusive_or_open || 'Open',
        special_incentives: res.data.special_incentives || '',
        agreement_valid_till: res.data.agreement_valid_till || '',
        notes: res.data.notes || '',
        assigned_bd_owner: res.data.assigned_bd_owner || '',
        stage: res.data.stage || 'Prospect',
      });
    } catch (error) {
      console.error('Failed to load project:', error);
      toast.error('Project not found');
      navigate('/bd/pipeline');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.builder_id) {
      toast.error('Please select a builder');
      return;
    }
    if (!formData.project_name.trim()) {
      toast.error('Project name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        brokerage_percent: formData.brokerage_percent ? parseFloat(formData.brokerage_percent) : null,
      };
      
      if (isNew) {
        const res = await api.createBDProject(payload);
        toast.success('Project created successfully');
        navigate(`/bd/projects/${res.data.bd_project_id}`);
      } else {
        await api.updateBDProject(projectId, payload);
        toast.success('Project updated successfully');
        setEditing(false);
        fetchProject();
      }
    } catch (error) {
      console.error('Failed to save project:', error);
      toast.error(error.response?.data?.detail || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const getStageBadge = (stage) => {
    const colors = {
      'Prospect': 'bg-slate-100 text-slate-600',
      'Intro Meeting Done': 'bg-blue-100 text-blue-700',
      'Commercial Discussion': 'bg-amber-100 text-amber-700',
      'Terms Locked': 'bg-purple-100 text-purple-700',
      'Agreement Signed': 'bg-emerald-100 text-emerald-700',
      'Live': 'bg-green-100 text-green-700',
      'Inactive': 'bg-red-100 text-red-700',
    };
    return <Badge className={colors[stage] || 'bg-slate-100'}>{stage}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft size={18} />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">
                  {isNew ? 'New Project' : formData.project_name}
                </h1>
                {!isNew && project && (
                  <div className="flex items-center gap-2 mt-1">
                    {getStageBadge(project.stage)}
                    <span className="text-sm text-slate-500">{project.builder_name}</span>
                  </div>
                )}
              </div>
            </div>
            
            {!isNew && !editing && (
              <Button onClick={() => setEditing(true)}>
                <Edit2 size={16} className="mr-1" />
                Edit
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {editing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Card className="p-4">
              <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Building2 size={18} className="text-purple-600" />
                Project Details
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Builder *</Label>
                  <Select 
                    value={formData.builder_id} 
                    onValueChange={(v) => setFormData({ ...formData, builder_id: v })}
                    disabled={!isNew && !!project}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select builder" />
                    </SelectTrigger>
                    <SelectContent>
                      {builders.map(b => (
                        <SelectItem key={b.builder_id} value={b.builder_id}>
                          {b.builder_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Project Name *</Label>
                  <Input
                    value={formData.project_name}
                    onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                    placeholder="Enter project name"
                  />
                </div>

                <div>
                  <Label>Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Project location"
                  />
                </div>

                <div>
                  <Label>Brokerage %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.brokerage_percent}
                    onChange={(e) => setFormData({ ...formData, brokerage_percent: e.target.value })}
                    placeholder="2.0"
                  />
                </div>

                <div>
                  <Label>Exclusive / Open</Label>
                  <Select 
                    value={formData.exclusive_or_open} 
                    onValueChange={(v) => setFormData({ ...formData, exclusive_or_open: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Exclusive">Exclusive</SelectItem>
                      <SelectItem value="Open">Open</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Stage</Label>
                  <Select 
                    value={formData.stage} 
                    onValueChange={(v) => setFormData({ ...formData, stage: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STAGES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Agreement Valid Till</Label>
                  <Input
                    type="date"
                    value={formData.agreement_valid_till}
                    onChange={(e) => setFormData({ ...formData, agreement_valid_till: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Assigned BD Owner</Label>
                  <Select 
                    value={formData.assigned_bd_owner} 
                    onValueChange={(v) => setFormData({ ...formData, assigned_bd_owner: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent>
                      {bdOwners.map(owner => (
                        <SelectItem key={owner.user_id} value={owner.user_id}>
                          {owner.rep_name || owner.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <Label>Special Incentives</Label>
                  <Textarea
                    value={formData.special_incentives}
                    onChange={(e) => setFormData({ ...formData, special_incentives: e.target.value })}
                    placeholder="Any special incentives..."
                    rows={2}
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={3}
                  />
                </div>
              </div>
            </Card>

            <div className="flex gap-2">
              <Button type="submit" disabled={saving} className="flex-1 bg-purple-600 hover:bg-purple-700">
                <Save size={16} className="mr-1" />
                {saving ? 'Saving...' : 'Save Project'}
              </Button>
              {!isNew && (
                <Button type="button" variant="outline" onClick={() => {
                  setEditing(false);
                  fetchProject();
                }}>
                  <X size={16} className="mr-1" />
                  Cancel
                </Button>
              )}
            </div>
          </form>
        ) : project && (
          <>
            {/* Project Info */}
            <Card className="p-4 mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Builder</p>
                  <p className="font-medium text-slate-800">{project.builder_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Location</p>
                  <p className="font-medium text-slate-800">{project.location || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Brokerage</p>
                  <p className="font-medium text-slate-800">{project.brokerage_percent ? `${project.brokerage_percent}%` : '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Type</p>
                  <p className="font-medium text-slate-800">{project.exclusive_or_open}</p>
                </div>
              </div>

              {project.special_incentives && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-slate-500 uppercase mb-1">Special Incentives</p>
                  <p className="text-sm text-slate-700">{project.special_incentives}</p>
                </div>
              )}

              {project.notes && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-slate-500 uppercase mb-1">Notes</p>
                  <p className="text-sm text-slate-700">{project.notes}</p>
                </div>
              )}
            </Card>

            {/* Stage History */}
            {project.stage_history && project.stage_history.length > 0 && (
              <Card className="p-4">
                <h3 className="font-semibold text-slate-800 mb-3">Stage History</h3>
                <div className="space-y-2">
                  {project.stage_history.map((entry, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-2">
                        {getStageBadge(entry.stage)}
                      </div>
                      <div className="text-right text-sm text-slate-500">
                        <p>{new Date(entry.entered_at).toLocaleDateString('en-IN')}</p>
                        {entry.duration_days !== undefined && (
                          <p className="text-xs">{entry.duration_days} days</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {project.total_onboarding_days > 0 && (
                  <p className="text-sm text-purple-600 mt-3 pt-3 border-t">
                    Total in pipeline: {project.total_onboarding_days} days
                  </p>
                )}
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BDProjectForm;
